import { useLazyQuery } from '@apollo/client'
import {
    BookingGetDailyPriceQuery,
    BookingGetDailyPriceQueryVariables,
    BookingPricingModelType
} from 'parkdepot-shared/gql/graphql'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckoutContext } from '../../../../context/CheckoutContext/checkout.context'
import { LotContext } from '../../../../context/LotContext/lot.context'
import { GET_DAILY_PRICE } from '../../../../graphql/queries/getDailyPrice.query'
import { Lot, LotViewName } from '../../../../types/lot.interface'
import { getNightParkingTimesFromConfigMemoized } from '../../../../utils/getNightParkingTimesFromConfig.utils'
import {
    NightParkingInnerWrapper,
    DatepickerWrapper,
    DurationWarningText,
    DurationWarningTitle
} from './NightParking.style'
import DailyBooking from '../DailyBooking/DailyBooking.component'
import datetimeToStartOrEndDatetime from '@utils/datetimeToStartOrEndDatetime.utils'
import { generateDailyPriceQueryOptions } from '@views/Lot/utils/generateDailyPriceQueryOptions.utils'
import { useGetUser } from '@hooks/useAuth'
import { useHistory } from 'react-router-dom'
import { lotRoute } from '@views/Lot/route'

export enum DurationType {
    NIGHT = 'night',
    WEEK = 'week',
    MONTH = 'month'
}

const getTimeNow = (): string => {
    const now = new Date()
    return `${now.getHours()}:${now.getMinutes()}`
}

const NightParking = () => {
    const { t } = useTranslation()
    const isMounted = useRef(false)
    const { user } = useGetUser()
    const history = useHistory()

    const { lot, setNextButtonLoading, setNextDisabled } = useContext(LotContext)
    const { modifyAndSetNightlyCheckout } = useContext(CheckoutContext)

    const [times, setTimes] = useState<{ start_time: string; end_time: string }>()
    const [sundayIncluded, setSundayIncluded] = useState<boolean>(false)
    const [, setDatepickerOpen] = useState(false)
    const [datepickerStartDate, setDatepickerStartDate] = useState<Date | null>(null)
    const [datepickerEndDate, setDatepickerEndDate] = useState<Date | null>(null)

    const [getDailyPrice] = useLazyQuery<BookingGetDailyPriceQuery, BookingGetDailyPriceQueryVariables>(GET_DAILY_PRICE)

    const earliestCalendarBookingDate =
        lot && lot.earliest_booking_date && new Date(lot.earliest_booking_date) > new Date()
            ? new Date(lot.earliest_booking_date)
            : new Date()
    let showBookCurrentNightHint = false

    const timeNow = getTimeNow()
    if (times && times.end_time && timeNow > '00:00' && timeNow < times.end_time) {
        // night is not over yet, so we should still allow booking for the current night
        earliestCalendarBookingDate.setDate(earliestCalendarBookingDate.getDate() - 1)
        showBookCurrentNightHint = true
    }

    const onDatesChange = useCallback(
        async (startDate: Date | null, endDate: Date | null) => {
            if (!lot || !startDate || !endDate) return

            if (startDate !== null && endDate !== null) {
                setDatepickerStartDate(startDate)
                setDatepickerEndDate(endDate)
                setDatepickerOpen(false)
                setNextButtonLoading(true)
                const start = datetimeToStartOrEndDatetime(startDate)
                const end = datetimeToStartOrEndDatetime(endDate, false)

                try {
                    const { data: priceData, error } = await getDailyPrice(
                        generateDailyPriceQueryOptions(lot.id, start, end, BookingPricingModelType.PublicNightly)
                    )
                    if (!isMounted.current) return
                    if (lot) {
                        modifyAndSetNightlyCheckout(lot, priceData?.booking_get_price?.price || 0, start, end)
                        setNextDisabled(false)
                    }
                    if (error) {
                        // TODO: handle error, depends how BE returns it
                    }
                } finally {
                    setNextButtonLoading(false)
                }
            }
        },
        [lot, user, modifyAndSetNightlyCheckout, setNextButtonLoading, setNextDisabled, getDailyPrice]
    )

    useEffect(() => {
        if (lot) {
            const feature = lot.active_features.find(
                (f) => f.name.toLocaleLowerCase() === LotViewName.NIGHTLY.toLowerCase()
            )
            if (!feature) {
                history.push(lotRoute(lot.id))
                return
            }
            if ((feature.config.night_parking_times?.length ?? 0) > 1) {
                setSundayIncluded(true)
            }
            const { start, end } = getNightParkingTimesFromConfigMemoized(feature)
            setTimes({ start_time: start, end_time: end })
        }
    }, [lot])

    useEffect(() => {
        isMounted.current = true
        return () => {
            isMounted.current = false
        }
    }, [])

    return (
        <>
            <NightParkingInnerWrapper>
                <DatepickerWrapper data-testid="nightly-booking-datepicker">
                    <DailyBooking
                        lot={lot as Lot}
                        startDate={datepickerStartDate}
                        endDate={datepickerEndDate}
                        datepickerVisibilityChange={setDatepickerOpen}
                        onDatesChanges={onDatesChange}
                    />
                </DatepickerWrapper>
                {showBookCurrentNightHint && (
                    <DurationWarningText style={{ marginTop: '20px' }}>
                        {t('lot.bookCurrentNightHint')}
                    </DurationWarningText>
                )}
            </NightParkingInnerWrapper>
            <NightParkingInnerWrapper>
                <DurationWarningTitle>{t('lot.beCareful')}</DurationWarningTitle>
                <DurationWarningText>
                    {t('lot.carefulMessage', {
                        start_time: times?.start_time,
                        end_time: times?.end_time
                    })}
                    {sundayIncluded && t('lot.sundayEntireDay')}
                </DurationWarningText>
            </NightParkingInnerWrapper>
        </>
    )
}

export default NightParking
